import type { ReactNode } from 'react';
import { Button } from './Button.tsx';
import { Icon, type IconName } from './Icon.tsx';
import { cx } from './util.ts';
import './States.css';

type HeadingLevel = 1 | 2 | 3 | 4;

export interface EmptyStateProps {
  title: ReactNode;
  description?: ReactNode;
  icon?: IconName;
  /** Acción para salir del vacío («Cargar documentos de ejemplo»). */
  action?: ReactNode;
  headingLevel?: HeadingLevel;
  className?: string;
}

/** Vacío diseñado: dice qué falta y qué hacer, nunca una pantalla en blanco. */
export function EmptyState({
  title,
  description,
  icon = 'documento',
  action,
  headingLevel = 3,
  className,
}: EmptyStateProps) {
  const Heading = `h${headingLevel}` as const;
  return (
    <div className={cx('af-state', 'af-state--empty', className)}>
      <span className="af-state__icon">
        <Icon name={icon} size="2rem" />
      </span>
      <Heading className="af-state__title">{title}</Heading>
      {description && <p className="af-state__description">{description}</p>}
      {action && <div className="af-state__action">{action}</div>}
    </div>
  );
}

export interface ErrorStateProps {
  /** Error en lenguaje de negocio: «No hemos podido cargar los documentos». */
  title?: ReactNode;
  description?: ReactNode;
  onRetry?: () => void;
  retryLabel?: string;
  headingLevel?: HeadingLevel;
  className?: string;
}

export function ErrorState({
  title = 'No hemos podido cargar los datos',
  description = 'Comprueba la conexión y vuelve a intentarlo.',
  onRetry,
  retryLabel = 'Reintentar',
  headingLevel = 3,
  className,
}: ErrorStateProps) {
  const Heading = `h${headingLevel}` as const;
  return (
    <div role="alert" className={cx('af-state', 'af-state--error', className)}>
      <span className="af-state__icon">
        <Icon name="estado-error" size="2rem" />
      </span>
      <Heading className="af-state__title">{title}</Heading>
      {description && <p className="af-state__description">{description}</p>}
      {onRetry && (
        <div className="af-state__action">
          <Button variant="primary" onClick={onRetry}>
            {retryLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

export interface SkeletonProps {
  /** text: líneas de texto; card: bloque con título y líneas; stat: cifra grande. */
  variant?: 'text' | 'card' | 'stat';
  lines?: number;
  /** Lo que se está cargando, para lectores de pantalla. */
  label?: string;
  className?: string;
}

/** Marcador de carga con la forma del contenido; con movimiento reducido no pulsa. */
export function Skeleton({
  variant = 'text',
  lines = 3,
  label = 'Cargando…',
  className,
}: SkeletonProps) {
  return (
    <div
      role="status"
      aria-label={label}
      aria-busy="true"
      className={cx('af-skeleton', `af-skeleton--${variant}`, className)}
    >
      {variant === 'stat' ? (
        <>
          <span className="af-skeleton__bar af-skeleton__bar--label" />
          <span className="af-skeleton__bar af-skeleton__bar--stat" />
        </>
      ) : (
        <>
          {variant === 'card' && <span className="af-skeleton__bar af-skeleton__bar--title" />}
          {Array.from({ length: lines }, (_, i) => (
            <span key={i} className="af-skeleton__bar" />
          ))}
        </>
      )}
    </div>
  );
}

/** Tecla o combinación: <Kbd>Esc</Kbd>, <Kbd keys={['Ctrl', 'K']} />. */
export function Kbd({ children, keys }: { children?: ReactNode; keys?: readonly string[] }) {
  if (keys) {
    return (
      <kbd className="af-kbd-combo">
        {keys.map((k, i) => (
          <span key={k}>
            {i > 0 && <span aria-hidden="true"> + </span>}
            <kbd className="af-kbd">{k}</kbd>
          </span>
        ))}
      </kbd>
    );
  }
  return <kbd className="af-kbd">{children}</kbd>;
}
